import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import {
  addGroupBy,
  addMeasure,
  availableAggs,
  numericColumns,
  parseEdges,
  removeGroupBy,
  removeMeasure,
  setBucketEdges,
  setDateGranularity,
  setGroupByColumn,
  setMeasureAgg,
  setPivotColumn,
  setSubtotals,
  updateMeasure,
} from '../../reporting/definitionUpdates';
import {
  NO_RELATED,
  refLabel,
  resolveColumnMeta,
  type RelatedManifests,
} from '../../reporting/columnRefs';
import { optionGroups, renderOptionGroups } from './columnOptions';
import TableColumnsEditor from './TableColumnsEditor';
import type {
  Agg,
  DatasetManifestEntry,
  DateGranularity,
  ReportDefinition,
} from '../../types/reporting';

const AGG_LABELS: Record<Agg, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
  share: 'Share',
};

const GRANULARITIES: { value: DateGranularity; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'quarter', label: 'Quarter' },
];

const AGING_EDGES = [31, 61, 91, 121, 151, 181, 211, 241];
const MAX_GROUP_BYS = 4;

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <label className='text-xs font-semibold uppercase text-muted-foreground'>
    {children}
  </label>
);

interface FieldPanelProps {
  dataset: DatasetManifestEntry;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
  related?: RelatedManifests;
}

const FieldPanel = ({
  dataset,
  definition,
  onChange,
  related = NO_RELATED,
}: FieldPanelProps) => {
  const isPatientRoster = definition.pipeline?.type === 'patient_roster';
  const numberColumns = numericColumns(dataset);
  const aggs = availableAggs(dataset);
  const aggregateIntent =
    !isPatientRoster &&
    (definition.group_bys.length > 0 || definition.measures.length > 0);
  const canAddMeasure = addMeasure(definition, dataset) !== definition;
  const canRemoveMeasure =
    definition.measures.length > 1 || !definition.group_bys.length;
  const hasAdditiveMeasure = definition.measures.some(
    measure => measure.agg === 'count' || measure.agg === 'sum'
  );

  return (
    <div className='min-w-0 space-y-5 overflow-x-hidden'>
      <TableColumnsEditor
        dataset={dataset}
        definition={definition}
        related={related}
        onChange={onChange}
      />

      {!isPatientRoster && (
        <div className='space-y-2'>
          <SectionLabel>Group by</SectionLabel>
          {definition.group_bys.map((groupBy, index) => {
            const meta = resolveColumnMeta(groupBy.column, dataset, related);
            const columnLabel = refLabel(groupBy.column, dataset, related);
            return (
              <div key={index} className='space-y-1 rounded-md border p-2'>
                <div className='flex items-center gap-1'>
                  <Select
                    value={groupBy.column}
                    onValueChange={value =>
                      onChange(
                        setGroupByColumn(
                          definition,
                          index,
                          value,
                          dataset,
                          related
                        )
                      )
                    }
                  >
                    <SelectTrigger
                      selectSize='sm'
                      className='flex-1'
                      title={columnLabel}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {renderOptionGroups(
                        optionGroups(
                          dataset,
                          related,
                          ref =>
                            ref === groupBy.column ||
                            !definition.group_bys.some(
                              (group, groupIndex) =>
                                groupIndex !== index && group.column === ref
                            )
                        )
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    size='sm'
                    variant='ghost'
                    aria-label={`Remove ${columnLabel} grouping`}
                    onClick={() => onChange(removeGroupBy(definition, index))}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
                {meta?.type === 'number' && (
                  <div className='space-y-1'>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() =>
                        onChange(setBucketEdges(definition, index, AGING_EDGES))
                      }
                    >
                      0-30 · 31-60 · 61-90 · 91-120 · 121-150 · 151-180 ·
                      181-210 · 211-240 · 241+
                    </Button>
                    <Label htmlFor={`ranges-${index}`}>Ranges</Label>
                    <Input
                      id={`ranges-${index}`}
                      key={
                        groupBy.transform?.type === 'bucket'
                          ? groupBy.transform.edges.join(',')
                          : 'exact'
                      }
                      inputSize='sm'
                      placeholder='e.g. 31, 61, 91'
                      defaultValue={
                        groupBy.transform?.type === 'bucket'
                          ? groupBy.transform.edges.join(', ')
                          : ''
                      }
                      onBlur={event =>
                        onChange(
                          setBucketEdges(
                            definition,
                            index,
                            parseEdges(event.target.value)
                          )
                        )
                      }
                    />
                    <p className='text-xs text-muted-foreground'>
                      Leave blank to show exact values
                    </p>
                  </div>
                )}
                {meta?.type === 'date' && (
                  <Select
                    value={
                      groupBy.transform?.type === 'date_trunc'
                        ? groupBy.transform.unit
                        : 'exact'
                    }
                    onValueChange={value =>
                      onChange(
                        setDateGranularity(
                          definition,
                          index,
                          value === 'exact' ? null : (value as DateGranularity)
                        )
                      )
                    }
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='exact'>Exact date</SelectItem>
                      {GRANULARITIES.map(granularity => (
                        <SelectItem
                          key={granularity.value}
                          value={granularity.value}
                        >
                          By {granularity.label.toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
          {definition.group_bys.length < MAX_GROUP_BYS && (
            <Select
              value=''
              onValueChange={value =>
                onChange(addGroupBy(definition, value, dataset, related))
              }
            >
              <SelectTrigger selectSize='sm'>
                <SelectValue placeholder='+ Add group by' />
              </SelectTrigger>
              <SelectContent>
                {renderOptionGroups(
                  optionGroups(
                    dataset,
                    related,
                    ref =>
                      !definition.group_bys.some(group => group.column === ref)
                  )
                )}
              </SelectContent>
            </Select>
          )}
          {definition.group_bys.length >= 2 && (
            <div className='space-y-1 pt-1'>
              <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                <Checkbox
                  checkboxSize='sm'
                  id='subtotals-toggle'
                  checked={!!definition.subtotals}
                  disabled={!hasAdditiveMeasure && !definition.subtotals}
                  onCheckedChange={checked =>
                    onChange(setSubtotals(definition, checked === true))
                  }
                />
                <label
                  className='cursor-pointer leading-4'
                  htmlFor='subtotals-toggle'
                >
                  Show subtotals by{' '}
                  {refLabel(definition.group_bys[0].column, dataset, related)}
                </label>
              </div>
              {!hasAdditiveMeasure && (
                <p className='text-xs text-muted-foreground'>
                  Add a Count or Sum value to calculate subtotals.
                </p>
              )}
              {definition.view === 'bar' && !!definition.subtotals && (
                <p className='text-xs text-muted-foreground'>
                  Subtotals appear in the table view only.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {aggregateIntent && (
        <div className='space-y-5'>
          <div className='space-y-2'>
            <SectionLabel>Show values</SectionLabel>
            {numberColumns.length === 0 && (
              <p className='text-xs text-muted-foreground'>
                {dataset.label} can only be counted
              </p>
            )}
            {definition.measures.map((measure, index) => (
              <div key={index} className='flex items-center gap-1'>
                {measure.agg === 'share' ? (
                  <span className='flex-1 text-sm'>
                    {refLabel(measure.column ?? '', dataset, related)} / (
                    {refLabel(measure.column ?? '', dataset, related)} +{' '}
                    {refLabel(measure.plus ?? '', dataset, related)})
                  </span>
                ) : (
                  <Select
                    value={measure.agg}
                    onValueChange={value =>
                      onChange(
                        setMeasureAgg(definition, index, value as Agg, dataset)
                      )
                    }
                  >
                    <SelectTrigger selectSize='sm' className='w-24'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aggs.map(agg => (
                        <SelectItem key={agg} value={agg}>
                          {AGG_LABELS[agg]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {measure.agg !== 'count' && measure.agg !== 'share' && (
                  <Select
                    value={measure.column ?? ''}
                    onValueChange={value =>
                      onChange(
                        updateMeasure(definition, index, {
                          agg: measure.agg,
                          column: value,
                        })
                      )
                    }
                  >
                    <SelectTrigger selectSize='sm' className='flex-1'>
                      <SelectValue placeholder='Column' />
                    </SelectTrigger>
                    <SelectContent>
                      {numberColumns
                        .filter(
                          column =>
                            column.name === measure.column ||
                            !definition.measures.some(
                              (other, otherIndex) =>
                                otherIndex !== index &&
                                other.agg === measure.agg &&
                                other.column === column.name
                            )
                        )
                        .map(column => (
                          <SelectItem key={column.name} value={column.name}>
                            {column.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
                {canRemoveMeasure && (
                  <Button
                    size='sm'
                    variant='ghost'
                    aria-label='Remove value'
                    onClick={() => onChange(removeMeasure(definition, index))}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
            ))}
            {canAddMeasure && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => onChange(addMeasure(definition, dataset))}
              >
                <Plus className='h-4 w-4' /> Add value
              </Button>
            )}
          </div>
          <div className='space-y-2'>
            <SectionLabel>Break out into columns</SectionLabel>
            <Select
              value={definition.pivot_column?.column ?? 'none'}
              onValueChange={value =>
                onChange(
                  setPivotColumn(
                    definition,
                    value === 'none' ? null : value,
                    dataset,
                    related
                  )
                )
              }
            >
              <SelectTrigger selectSize='sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>None</SelectItem>
                {renderOptionGroups(optionGroups(dataset, related))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldPanel;
